/**
 * Configuración raíz (`ApplicationConfig`) de la aplicación **declarabid-private**.
 *
 * Puntos relevantes para documentación técnica:
 *
 * - **HTTP:** `provideHttpClient(withInterceptors([authInterceptor]))` — capa de autenticación
 *   en todas las peticiones salientes (véase {@link authInterceptor}).
 * - **Router:** rutas principales en {@link routes}; el árbol detallado por rol está en
 *   `mainAppRoutes` (`app.main.routes.ts`).
 * - **PrimeNG / tema:** `providePrimeNG` con preset DeclaraBID.
 *
 * @see authInterceptor
 * @see routes
 */

import {
  ApplicationConfig,
  importProvidersFrom,
  LOCALE_ID,
} from '@angular/core';
import {
  InMemoryScrollingFeature,
  InMemoryScrollingOptions,
  provideRouter,
  withInMemoryScrolling,
  withNavigationErrorHandler,
} from '@angular/router';

import { provideHttpClient, withInterceptors } from '@angular/common/http';

import { routes } from './app.routes';
import { CookieService } from 'ngx-cookie-service';
import { authInterceptor } from './features/auth/services/auth-interceptor.service';

import { RECAPTCHA_LOADER_OPTIONS } from 'ng-recaptcha-2';
import { MessageService } from 'primeng/api';

import { providePrimeNG } from 'primeng/config';
import { DeclaraBIDPreset } from './theme/primeng/present';
import { handleChunkLoadError } from './core/utils/chunk-load-recovery.util';

const scrollConfig: InMemoryScrollingOptions = {
  scrollPositionRestoration: 'top',
  anchorScrolling: 'enabled',
};

const inMemoryScrollingFeature: InMemoryScrollingFeature =
  withInMemoryScrolling(scrollConfig);

export const appConfig: ApplicationConfig = {
  providers: [
    provideRouter(
      routes,
      inMemoryScrollingFeature,
      withNavigationErrorHandler((error) => {
        if (handleChunkLoadError(error)) {
          return;
        }

        throw error;
      }),
    ),
    provideHttpClient(withInterceptors([authInterceptor])),
    importProvidersFrom(CookieService),
    providePrimeNG({
      theme: {
        preset: DeclaraBIDPreset,
        options: {
          darkModeSelector: false,
        },
      },
      zIndex: {
        modal: 1100,
        overlay: 1000,
        menu: 1000,
        tooltip: 1100,
      },
    }),
    MessageService,
    {
      provide: RECAPTCHA_LOADER_OPTIONS,
      useFactory: (locale: string) => ({
        onBeforeLoad(url: { searchParams: URLSearchParams }) {
          url.searchParams.set('hl', locale);
          return { url };
        },
      }),
      deps: [LOCALE_ID],
    },
  ],
};
